import { useEffect, useMemo, useState } from "react";

const sectionTitle = {
  fontSize: 24,
  fontWeight: 800,
  marginBottom: 10,
};

const industryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  
  gap: 16,
  marginTop: 4
};

const industryWrapper = {
  width: "100%",
  display: "flex",
  justifyContent: "center"
};

const industryInner = {
  width: "90%",
  maxWidth: 900   // keeps desktop looking good
};


/**
 * Industry registry:
 * - Keep the full list (no removals)
 * - Modal opens for all items
 * - If url is missing / "#", we show "Coming Soon" behavior inside modal.
 */
const INDUSTRIES = [
  {
    title: "Junk Removal",
    active: true,
    icon: "/images/junkyard-jeff-icon.png",
    activeLabel: "AI Junk Removal Estimates",
    activeUrl: "https://showmeyourjunk.biz",
    url: "https://showmeyourjunk.biz",
    description:
      "AI-powered photo-based estimates for junk removal businesses. Capture accurate volume, weight, and item counts directly from customer uploads — without site visits."
  },
  {
    title: "Roofing",
    comingSoon: true,
    description:
      "Pre-qualify roofing jobs using customer photos to estimate scope before rolling a truck.",
    url: "#"
  },
  {
    title: "Landscaping & Yard Cleanup",
    comingSoon: true,
    description:
      "Convert yard photos into structured cleanup estimates and faster scheduling.",
    url: "#"
  },
  {
    title: "Tree Trimming & Removal",
    comingSoon: true,
    description:
      "Estimate trimming/removal scope from photos and capture key job details automatically.",
    url: "#"
  },
  {
    title: "Painting (Interior & Exterior)",
    comingSoon: true,
    description:
      "Gather room/exterior photos and turn them into structured job scope and next steps.",
    url: "#"
  },
  {
    title: "Flooring Installation",
    comingSoon: true,
    description:
      "Capture room measurements, floor type, and job complexity from photo-based intake.",
    url: "#"
  },
  {
    title: "Water / Fire / Mold Restoration",
    comingSoon: true,
    description:
      "Photo-driven damage intake and triage to speed up response and reduce back-and-forth.",
    url: "#"
  },
  {
    title: "Pool Services",
    comingSoon: true,
    description:
      "Turn pool/equipment photos into service estimates and scheduling-ready requests.",
    url: "#"
  },
  {
    title: "Fence Installation & Repair",
    comingSoon: true,
    description:
      "Capture fence-line photos and job context to create structured repair/install requests.",
    url: "#"
  },
  {
    title: "Pest & Wildlife Control",
    comingSoon: true,
    description:
      "Collect photos of damage/entry points and generate the right next steps automatically.",
    url: "#"
  },
  {
    title: "Concrete & Driveway Repair",
    comingSoon: true,
    description:
      "Photo-based intake for cracks, spalling, and surface issues to estimate scope faster.",
    url: "#"
  },
  {
    title: "Moving Services",
    comingSoon: true,
    description:
      "Use room photos to estimate load size and logistics before quoting or scheduling.",
    url: "#"
  },
  {
    title: "Home Cleaning",
    comingSoon: true,
    description:
      "Photo-based intake to identify cleaning needs and produce structured requests.",
    url: "#"
  },
  {
    title: "Garage Door Services",
    comingSoon: true,
    description:
      "Collect door/opener photos and symptoms to triage issues and streamline dispatch.",
    url: "#"
  },
  {
    title: "Insurance Claims Intake",
    comingSoon: true,
    description:
      "Capture photo evidence and structured details to accelerate claims workflows.",
    url: "#"
  }
];

export default function SupportedIndustriesSection() {
  const [activeIndustryTitle, setActiveIndustryTitle] = useState(null);

  const activeIndustry = useMemo(() => {
    if (!activeIndustryTitle) return null;
    return INDUSTRIES.find((i) => i.title === activeIndustryTitle) || null;
  }, [activeIndustryTitle]);

  // ESC to close + body scroll lock while modal open
  useEffect(() => {
    if (!activeIndustryTitle) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setActiveIndustryTitle(null);
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeIndustryTitle]);

  return (
    <section>
      <h2 style={sectionTitle}>Supported Industries</h2>

      <div style={industryWrapper}>
        <div style={industryInner}>
          <div style={industryGrid}>
            {INDUSTRIES.map((industry) => (
              <IndustryCard
                key={industry.title}
                {...industry}
                onClick={() => setActiveIndustryTitle(industry.title)}
              />
            ))}
          </div>
        </div>
      </div>


      <IndustryModal
        industry={activeIndustry}
        onClose={() => setActiveIndustryTitle(null)}
      />
    </section>
  );
}

function IndustryCard({
  title,
  active,
  comingSoon,
  icon,
  activeLabel,
  activeUrl,
  onClick
}) {
  const [hovered, setHovered] = useState(false);

  const backgroundColor = active
    ? hovered
      ? "#FEF9C3"
      : "#ffffff"
    : "#f1f5f9";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      onMouseEnter={() => active && setHovered(true)}
      onMouseLeave={() => active && setHovered(false)}
      style={{
        padding: 8,
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        background: backgroundColor,
        transition: "background-color 0.2s ease",
        color: active ? "#0f172a" : "#94a3b8",
        display: "flex",
        alignItems: "center",
        gap: 5,
        minHeight: 78,
        cursor: "pointer",
        userSelect: "none"
      }}
      title="Click for details"
    >
      {icon && (
        <img src={icon} alt="Industry icon" style={{ width: 68, height: 68 }} />
      )}

      <div>
        <strong style={{ fontSize: 19 }}>{title}</strong>

        <div
          style={{
            fontSize: 12,
            marginTop: 2,
            color: active ? "#2563eb" : "#94a3b8"
          }}
        >
          {active ? activeLabel : comingSoon ? "Coming Soon" : ""}
        </div>

        {active && activeUrl && (
          <div style={{ fontSize: 13, color: "#64748b" }}>{activeUrl}</div>
        )}
      </div>
    </div>
  );
}

function IndustryModal({ industry, onClose }) {
  if (!industry) return null;

  const hasLiveLink = industry.url && industry.url !== "#";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "80%",
          height: "90%",
          background: "#ffffff",
          borderRadius: 20,
          padding: 28,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          overflowY: "auto",
          position: "relative"
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            border: "none",
            background: "transparent",
            fontSize: 22,
            cursor: "pointer",
            color: "#0f172a"
          }}
        >
          ✕
        </button>

        <div style={{ paddingRight: 34 }}>
          <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em" }}>
            {industry.title}
          </div>

          <div style={{ marginTop: 12, fontSize: 18, color: "#475569", maxWidth: 860, lineHeight: 1.55 }}>
            {industry.description || "Details coming soon."}
          </div>

          <div style={{ marginTop: 18, color: "#64748b", fontSize: 14 }}>
            {hasLiveLink ? "Live link:" : "Status:"}{" "}
            <span style={{ color: hasLiveLink ? "#0f172a" : "#2563eb", fontWeight: 700 }}>
              {hasLiveLink ? industry.url : "Coming Soon"}
            </span>
          </div>

          {hasLiveLink && (
            <a
              href={industry.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: 22,
                padding: "14px 18px",
                background: "#2563eb",
                color: "#ffffff",
                borderRadius: 12,
                textDecoration: "none",
                fontWeight: 700
              }}
            >
              Visit {industry.title} →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
